/**
 * Organization Transactions Section
 *
 * Admin view for browsing and inspecting Stedi EDI transactions
 * (claims, acknowledgments, remittances, eligibility) for an organization.
 *
 * Uses the generated hooks from store/generated/stedi.ts which proxy
 * the Stedi Core API through our admin endpoints.
 */

import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { enhancedApi as stediApi } from '@store/generated/stedi';
import { formatRelativeTime, formatDateTime } from '../utils/formatting';
import { extractApiErrorMessage, extractApiErrorCode } from '@features/billing-readiness/utils/errorUtils';

// ============================================================================
// Types
// ============================================================================

interface Artifact {
  artifactType: string;
  usage: string;
  sizeBytes: number;
  url: string;
  model?: string;
}

interface StediTransaction {
  transactionId: string;
  fileExecutionId?: string;
  status: 'succeeded' | 'failed';
  direction: 'INBOUND' | 'OUTBOUND' | 'UNKNOWN';
  mode: string;
  processedAt: string;
  artifacts?: Artifact[];
  partnership?: {
    partnershipId?: string;
    partnershipType?: string;
    sender: { profileId: string };
    receiver: { profileId: string };
  };
  x12?: {
    metadata: {
      interchange: { controlNumber: number };
      functionalGroup: {
        controlNumber: number;
        release: string;
        date: string;
        time: string;
        functionalIdentifierCode: string;
      };
      transaction: {
        controlNumber: string;
        transactionSetIdentifier: string;
      };
      sender: { applicationCode: string };
      receiver: { applicationCode: string };
    };
  };
  businessIdentifiers?: Array<{
    elementId: string;
    element: string;
    name: string;
    value: string;
  }>;
  translationErrors?: Array<{
    message: string;
    context: { code: string };
  }>;
}

interface TransactionFilters {
  transactionSetId: string;
  direction: string;
  status: string;
  mode: string;
  fromDate: string;
  toDate: string;
}

// ============================================================================
// Constants
// ============================================================================

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  '837': '837P Claim',
  '277': '277CA Acknowledgment',
  '835': '835 Remittance',
  '270': '270 Eligibility Request',
  '271': '271 Eligibility Response',
  '276': '276 Status Request',
};

const TRANSACTION_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: '837', label: '837P Claim' },
  { value: '277', label: '277CA Acknowledgment' },
  { value: '835', label: '835 Remittance' },
  { value: '270', label: '270 Eligibility Request' },
  { value: '271', label: '271 Eligibility Response' },
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'All Directions' },
  { value: 'OUTBOUND', label: 'Sent' },
  { value: 'INBOUND', label: 'Received' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

const MODE_OPTIONS = [
  { value: '', label: 'All Modes' },
  { value: 'production', label: 'Production' },
  { value: 'test', label: 'Test' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DEFAULT_FILTERS: TransactionFilters = {
  transactionSetId: '',
  direction: '',
  status: '',
  mode: 'production',
  fromDate: '',
  toDate: '',
};

// ============================================================================
// Helpers
// ============================================================================

function getTypeLabel(transaction: StediTransaction): string {
  const code = transaction.x12?.metadata?.transaction?.transactionSetIdentifier;
  return code ? (TRANSACTION_TYPE_LABELS[code] ?? code) : 'Unknown';
}

function getTypeCode(transaction: StediTransaction): string {
  return transaction.x12?.metadata?.transaction?.transactionSetIdentifier ?? '';
}

function getDirectionDisplay(direction: string): { label: string; icon: 'out' | 'in' | 'unknown' } {
  switch (direction) {
    case 'OUTBOUND': return { label: 'Sent', icon: 'out' };
    case 'INBOUND': return { label: 'Received', icon: 'in' };
    default: return { label: 'Unknown', icon: 'unknown' };
  }
}

function getFirstBusinessId(transaction: StediTransaction): string | null {
  return transaction.businessIdentifiers?.[0]?.value ?? null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================================================
// Output Extraction Helpers
// ============================================================================

interface ClaimSummary {
  patientName: string | null;
  payerName: string | null;
  claimId: string | null;
  totalCharge: string | null;
  serviceDate: string | null;
  diagnosisCodes: string[];
  cptCodes: Array<{ code: string; charge: string }>;
  billingProvider: { name: string | null; npi: string | null };
  renderingProvider: { name: string | null; npi: string | null };
}

function extract837Summary(output: Record<string, unknown>): ClaimSummary | null {
  try {
    const heading = output?.heading as Record<string, unknown> | undefined;
    const detail = output?.detail as Record<string, unknown> | undefined;

    const subscriberLoop = (heading as any)?.subscriber_loop?.[0];
    const patient = subscriberLoop?.patient_loop?.[0]?.patient_name;
    const patientName = patient
      ? `${patient.name_first ?? ''} ${patient.name_last ?? ''}`.trim() || null
      : null;

    const payer = subscriberLoop?.payer_loop?.[0]?.payer_name;
    const payerName = payer?.name ?? null;

    const claims = (detail as any)?.claim_information ?? [];
    const firstClaim = claims[0];
    const claimId = firstClaim?.claim_submitters_identifier ?? null;
    const totalCharge = firstClaim?.total_claim_charge_amount ?? null;

    const serviceLines = firstClaim?.service_line_loop ?? [];
    const cptCodes = serviceLines.map((sl: any) => ({
      code: sl?.service_line?.procedure_code ?? '',
      charge: sl?.service_line?.line_item_charge_amount ?? '',
    })).filter((c: any) => c.code);

    const diagnosisCodes = firstClaim?.health_care_diagnosis_code?.diagnosis_codes ?? [];

    const billingProvider = (heading as any)?.billing_provider_loop?.[0];
    const billingNpi = billingProvider?.billing_provider_name?.npi ?? null;
    const billingName = billingProvider?.billing_provider_name?.name ?? null;

    const renderingProvider = firstClaim?.rendering_provider_loop?.[0];
    const renderingNpi = renderingProvider?.rendering_provider_name?.npi ?? null;
    const renderingName = renderingProvider?.rendering_provider_name
      ? `${renderingProvider.rendering_provider_name.name_first ?? ''} ${renderingProvider.rendering_provider_name.name_last ?? ''}`.trim() || null
      : null;

    const serviceDate = serviceLines[0]?.service_date?.date ?? null;

    return {
      patientName,
      payerName,
      claimId,
      totalCharge,
      serviceDate,
      diagnosisCodes,
      cptCodes,
      billingProvider: { name: billingName, npi: billingNpi },
      renderingProvider: { name: renderingName, npi: renderingNpi },
    };
  } catch {
    return null;
  }
}

interface AckClaimStatus {
  claimId: string | null;
  status: string | null;
  statusDescription: string | null;
  effectiveDate: string | null;
}

function extract277Summary(output: Record<string, unknown>): AckClaimStatus[] {
  try {
    const detail = output?.detail as Record<string, unknown> | undefined;
    const claims = (detail as any)?.claim_status_tracking ?? [];

    return claims.map((claim: any) => ({
      claimId: claim?.reference_identification ?? null,
      status: claim?.claim_status?.status_code ?? null,
      statusDescription: claim?.claim_status?.status_description ?? null,
      effectiveDate: claim?.claim_status?.effective_date ?? null,
    }));
  } catch {
    return [];
  }
}

interface RemittanceSummary {
  totalPaid: string | null;
  paymentMethod: string | null;
  paymentDate: string | null;
  checkNumber: string | null;
}

function extract835Summary(output: Record<string, unknown>): RemittanceSummary | null {
  try {
    const heading = output?.heading as Record<string, unknown> | undefined;
    const summary = (heading as any)?.financial_information;
    if (!summary) return null;

    return {
      totalPaid: summary?.total_actual_provider_payment_amount ?? null,
      paymentMethod: summary?.credit_debit_flag_code ?? null,
      paymentDate: summary?.check_issue_or_eft_effective_date ?? null,
      checkNumber: summary?.originator_reference_number ?? null,
    };
  } catch {
    return null;
  }
}

// ============================================================================
// Sub-Components
// ============================================================================

interface TransactionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  transactionId: string | null;
}

function TransactionDetailDialog({ open, onClose, organizationId, transactionId }: TransactionDetailDialogProps) {
  const theme = useTheme();
  const [rawExpanded, setRawExpanded] = useState(false);

  const { data, isLoading, error } = stediApi.useGetV2AdminOrgsByOrganizationIdStediTransactionsAndTransactionIdQuery(
    { organizationId, transactionId: transactionId ?? '' },
    { skip: !transactionId || !open },
  );

  const transaction = data?.transaction as StediTransaction | undefined;
  const output = data?.output as Record<string, unknown> | null | undefined;
  const typeCode = transaction ? getTypeCode(transaction) : '';

  const renderOutputSummary = () => {
    if (!output) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Output data is not available for this transaction.
        </Alert>
      );
    }

    if (typeCode === '837') {
      const summary = extract837Summary(output);
      if (!summary) return null;

      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Claim Details
          </Typography>
          <Grid container spacing={2}>
            {summary.patientName && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Patient</Typography>
                <Typography variant="body2">{summary.patientName}</Typography>
              </Grid>
            )}
            {summary.payerName && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Payer</Typography>
                <Typography variant="body2">{summary.payerName}</Typography>
              </Grid>
            )}
            {summary.claimId && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Claim ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{summary.claimId}</Typography>
              </Grid>
            )}
            {summary.totalCharge && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Total Charge</Typography>
                <Typography variant="body2">${summary.totalCharge}</Typography>
              </Grid>
            )}
            {summary.serviceDate && (
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">Service Date</Typography>
                <Typography variant="body2">{summary.serviceDate}</Typography>
              </Grid>
            )}
            {summary.diagnosisCodes.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Diagnosis Codes</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {summary.diagnosisCodes.map((code: string, i: number) => (
                    <Chip key={i} label={code} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}
            {summary.cptCodes.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Service Lines</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {summary.cptCodes.map((cpt, i) => (
                    <Chip key={i} label={`${cpt.code} ($${cpt.charge})`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>

          {(summary.billingProvider.name || summary.renderingProvider.name) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Providers</Typography>
              <Grid container spacing={2}>
                {summary.billingProvider.name && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Billing</Typography>
                    <Typography variant="body2">
                      {summary.billingProvider.name}
                      {summary.billingProvider.npi && (
                        <Typography component="span" variant="caption" color="text.secondary"> NPI {summary.billingProvider.npi}</Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
                {summary.renderingProvider.name && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Rendering</Typography>
                    <Typography variant="body2">
                      {summary.renderingProvider.name}
                      {summary.renderingProvider.npi && (
                        <Typography component="span" variant="caption" color="text.secondary"> NPI {summary.renderingProvider.npi}</Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Box>
      );
    }

    if (typeCode === '277') {
      const claims = extract277Summary(output);
      if (claims.length === 0) return null;

      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Claim Status
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Claim ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.map((claim, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{claim.claimId ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={claim.status ?? '—'}
                      size="small"
                      color={claim.status === 'A' ? 'success' : claim.status === 'R' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{claim.statusDescription ?? '—'}</TableCell>
                  <TableCell>{claim.effectiveDate ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
    }

    if (typeCode === '835') {
      const summary = extract835Summary(output);
      if (!summary) return null;

      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Payment Summary
          </Typography>
          <Grid container spacing={2}>
            {summary.totalPaid && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                <Typography variant="body2">${summary.totalPaid}</Typography>
              </Grid>
            )}
            {summary.paymentMethod && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Method</Typography>
                <Typography variant="body2">{summary.paymentMethod}</Typography>
              </Grid>
            )}
            {summary.paymentDate && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Payment Date</Typography>
                <Typography variant="body2">{summary.paymentDate}</Typography>
              </Grid>
            )}
            {summary.checkNumber && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Check / Reference #</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{summary.checkNumber}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Transaction Detail</span>
          <IconButton edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            {extractApiErrorMessage(error, 'Failed to load transaction details')}
          </Alert>
        )}

        {transaction && (
          <Box>
            {/* Header Summary */}
            <Box
              sx={{
                p: 2.5,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {getTypeLabel(transaction)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    — {getDirectionDisplay(transaction.direction).label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={transaction.status}
                    size="small"
                    color={transaction.status === 'succeeded' ? 'success' : 'error'}
                  />
                  <Chip label={transaction.mode} size="small" variant="outlined" />
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Processed: {formatDateTime(transaction.processedAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                ID: {transaction.transactionId}
              </Typography>
            </Box>

            {/* Translation Errors (for failed transactions) */}
            {transaction.translationErrors && transaction.translationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Translation Errors
                </Typography>
                {transaction.translationErrors.map((err, i) => (
                  <Typography key={i} variant="body2">
                    {err.message}
                  </Typography>
                ))}
              </Alert>
            )}

            {/* Output Summary (type-specific) */}
            {renderOutputSummary()}

            <Divider sx={{ my: 3 }} />

            {/* Business Identifiers */}
            {transaction.businessIdentifiers && transaction.businessIdentifiers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Business Identifiers
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Element</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transaction.businessIdentifiers.map((bi, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{bi.element}</TableCell>
                        <TableCell>{bi.name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{bi.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* EDI Metadata */}
            {transaction.x12?.metadata && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  EDI Metadata
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Interchange #</Typography>
                    <Typography variant="body2">{transaction.x12.metadata.interchange.controlNumber}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Group #</Typography>
                    <Typography variant="body2">{transaction.x12.metadata.functionalGroup.controlNumber}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Release</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {transaction.x12.metadata.functionalGroup.release}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Functional ID</Typography>
                    <Typography variant="body2">{transaction.x12.metadata.functionalGroup.functionalIdentifierCode}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Sender</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {transaction.x12.metadata.sender.applicationCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Receiver</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {transaction.x12.metadata.receiver.applicationCode}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Partnership */}
            {transaction.partnership && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Partnership
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Sender Profile</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {transaction.partnership.sender.profileId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Receiver Profile</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {transaction.partnership.receiver.profileId}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Artifacts */}
            {transaction.artifacts && transaction.artifacts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Artifacts
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {transaction.artifacts.map((artifact, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {artifact.usage} — {artifact.artifactType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatBytes(artifact.sizeBytes)}
                          {artifact.model && ` — ${artifact.model}`}
                        </Typography>
                      </Box>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => window.open(artifact.url, '_blank', 'noopener,noreferrer')}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Raw Output */}
            {output && (
              <Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setRawExpanded(!rawExpanded)}
                  endIcon={<ExpandMoreIcon sx={{ transform: rawExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                  sx={{ textTransform: 'none', mb: 1 }}
                >
                  Raw Output
                </Button>
                <Collapse in={rawExpanded}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.grey[50],
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {JSON.stringify(output, null, 2)}
                    </pre>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface OrganizationTransactionsSectionProps {
  organizationId: string;
  isConnected: boolean;
}

export function OrganizationTransactionsSection({ organizationId, isConnected }: OrganizationTransactionsSectionProps) {
  const theme = useTheme();

  // Filter state
  const [filters, setFilters] = useState<TransactionFilters>(DEFAULT_FILTERS);
  const [pageSize, setPageSize] = useState(25);

  // Pagination state (cursor-based)
  const [pageTokenStack, setPageTokenStack] = useState<string[]>([]);
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>();

  // Detail dialog state
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Build query args
  const queryArgs = {
    organizationId,
    pageSize,
    pageToken: currentPageToken ?? undefined,
    direction: filters.direction || undefined,
    status: filters.status || undefined,
    mode: filters.mode || undefined,
    transactionSetId: filters.transactionSetId || undefined,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
  };

  const { data, isLoading, isFetching, error, refetch } = stediApi.useGetV2AdminOrgsByOrganizationIdStediTransactionsQuery(
    queryArgs,
    { skip: !isConnected },
  );

  const transactions = (data?.items ?? []) as StediTransaction[];
  const nextPageToken = data?.next_page_token ?? null;

  // Detect specific error codes
  const errorCode = error ? extractApiErrorCode(error) : null;

  // Handlers
  const handleFilterChange = useCallback((key: keyof TransactionFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Reset pagination when filters change
    setPageTokenStack([]);
    setCurrentPageToken(undefined);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPageTokenStack([]);
    setCurrentPageToken(undefined);
  }, []);

  const handleNextPage = useCallback(() => {
    if (!nextPageToken) return;
    setPageTokenStack(prev => [...prev, currentPageToken ?? '']);
    setCurrentPageToken(nextPageToken);
  }, [nextPageToken, currentPageToken]);

  const handlePrevPage = useCallback(() => {
    const prev = pageTokenStack[pageTokenStack.length - 1];
    setPageTokenStack(tokens => tokens.slice(0, -1));
    setCurrentPageToken(prev || undefined);
  }, [pageTokenStack]);

  const handleRowClick = useCallback((transaction: StediTransaction) => {
    setSelectedTransactionId(transaction.transactionId);
    setDetailDialogOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Not connected state
  if (!isConnected) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          Connect to Stedi first to view transactions.
        </Typography>
      </Alert>
    );
  }

  // Auth error state
  if (errorCode === 'stedi_auth_error') {
    return (
      <Alert severity="error">
        <Typography variant="body2">
          Stedi API key is invalid or expired. Please update the connection.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.transactionSetId}
                  label="Type"
                  onChange={e => handleFilterChange('transactionSetId', e.target.value)}
                >
                  {TRANSACTION_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select
                  value={filters.direction}
                  label="Direction"
                  onChange={e => handleFilterChange('direction', e.target.value)}
                >
                  {DIRECTION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={e => handleFilterChange('status', e.target.value)}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select
                  value={filters.mode}
                  label="Mode"
                  onChange={e => handleFilterChange('mode', e.target.value)}
                >
                  {MODE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From"
                type="date"
                value={filters.fromDate}
                onChange={e => handleFilterChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To"
                type="date"
                value={filters.toDate}
                onChange={e => handleFilterChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transaction Table */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <CardContent>
          {/* Loading state */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error state */}
          {error && errorCode !== 'stedi_auth_error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorCode === 'stedi_api_error'
                ? 'Unable to reach Stedi — try again'
                : extractApiErrorMessage(error, 'Failed to load transactions')}
            </Alert>
          )}

          {/* Table */}
          {!isLoading && !error && (
            <>
              {transactions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Direction</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Processed</TableCell>
                        <TableCell>Mode</TableCell>
                        <TableCell>Reference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map(tx => {
                        const dir = getDirectionDisplay(tx.direction);
                        const ref = getFirstBusinessId(tx);

                        return (
                          <TableRow
                            key={tx.transactionId}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleRowClick(tx)}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getTypeLabel(tx)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {dir.icon === 'out' ? (
                                  <CallMadeIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                                ) : dir.icon === 'in' ? (
                                  <CallReceivedIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                                ) : null}
                                <Typography variant="body2">{dir.label}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={tx.status}
                                size="small"
                                color={tx.status === 'succeeded' ? 'success' : 'error'}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={formatDateTime(tx.processedAt)}>
                                <Typography variant="body2">{formatRelativeTime(tx.processedAt)}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={tx.mode}
                                size="small"
                                variant="outlined"
                                color={tx.mode === 'production' ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                                {ref ?? '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  <Typography variant="body2">
                    {pageTokenStack.length > 0 || currentPageToken
                      ? 'No more transactions found.'
                      : filters.transactionSetId || filters.direction || filters.status || (filters.mode && filters.mode !== 'production') || filters.fromDate || filters.toDate
                        ? 'No transactions found matching your filters.'
                        : 'No transactions yet. Transactions will appear here once claims are submitted or responses are received through Stedi.'}
                  </Typography>
                </Alert>
              )}

              {/* Pagination */}
              {(transactions.length > 0 || pageTokenStack.length > 0) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Page {pageTokenStack.length + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      — {transactions.length} results
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel>Per page</InputLabel>
                      <Select
                        value={pageSize}
                        label="Per page"
                        onChange={e => handlePageSizeChange(Number(e.target.value))}
                      >
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <MenuItem key={size} value={size}>{size}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        disabled={pageTokenStack.length === 0}
                        onClick={handlePrevPage}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!nextPageToken}
                        onClick={handleNextPage}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <TransactionDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedTransactionId(null);
        }}
        organizationId={organizationId}
        transactionId={selectedTransactionId}
      />
    </Box>
  );
}
